/**
 * [1:1] Parses the two date strings the SOF views hand back.
 *
 * Neither is an ISO string and NEITHER is parseable by `new Date()` or Angular's
 * `DatePipe` — passing `dateRequired` straight to the pipe throws NG02100 and
 * kills the card it was rendering, and `new Date(dateSort)` silently returns
 * Invalid Date, which made every row compare equal and left the queue sorted by
 * its tiebreak instead of by date.
 *
 * The two formats, both derived from the same `SOF.Orders.DeliveryDate`:
 *
 * - `dateRequired` — `CONVERT(VARCHAR, DeliveryDate, 100)`, e.g.
 *   `"Nov 17 2025  7:00AM"`. Style 100 space-pads the day and the hour, so the
 *   run of spaces is variable, and the meridiem has no space before it. This is
 *   the ONLY one of the two that carries a time.
 * - `dateSort` — `CONVERT(VARCHAR, DeliveryDate, 112)`, e.g. `"20251117"`.
 *   Date only; the time is discarded by the view.
 *
 * Because they are the same column, `dateRequired` is strictly more precise and
 * is what callers should prefer, falling back to `dateSort` only when it is
 * missing or malformed.
 */

const STYLE_100 =
  /^([A-Za-z]{3})\s+(\d{1,2})\s+(\d{4})\s+(\d{1,2}):(\d{2})\s*([AP]M)$/i;

const STYLE_112 = /^(\d{4})(\d{2})(\d{2})$/;

const MONTHS: Readonly<Record<string, number>> = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
};

/**
 * Parses either SQL format into a Date, or null when the value is absent or
 * doesn't match. Null rather than an Invalid Date so callers can branch on
 * "no usable date" without an `isNaN` dance at every call site.
 *
 * @param value - A `dateRequired` (style 100) or `dateSort` (style 112) string.
 * @returns The parsed local Date, or null.
 */
export function parseSofDate(value: string | null | undefined): Date | null {
  if (!value) {
    return null;
  }
  const text = value.trim();

  const style100 = STYLE_100.exec(text);
  if (style100) {
    const month = MONTHS[style100[1].toLowerCase()];
    if (month === undefined) {
      return null;
    }
    const day = Number(style100[2]);
    const year = Number(style100[3]);
    // 12AM is hour 0 and 12PM is hour 12 — the modulo handles both, so noon
    // doesn't land at midnight and sort a whole day early.
    const hour = Number(style100[4]) % 12 + (/pm/i.test(style100[6]) ? 12 : 0);
    const minute = Number(style100[5]);
    const parsed = new Date(year, month, day, hour, minute);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const style112 = STYLE_112.exec(text);
  if (style112) {
    const year = Number(style112[1]);
    const month = Number(style112[2]) - 1;
    const day = Number(style112[3]);
    const parsed = new Date(year, month, day);
    // Guard a value like "20251345": the Date constructor rolls overflow into
    // the next month rather than failing, so verify the parts survived.
    if (
      parsed.getFullYear() !== year ||
      parsed.getMonth() !== month ||
      parsed.getDate() !== day
    ) {
      return null;
    }
    return parsed;
  }

  return null;
}

/**
 * The best available Date for an order: `dateRequired` (date + time) when it
 * parses, otherwise `dateSort` (date only), otherwise null.
 *
 * @param order - Anything carrying the two raw view strings.
 * @returns The most precise usable Date, or null when neither parses.
 */
export function orderDate(order: {
  dateRequired?: string | null;
  dateSort?: string | null;
}): Date | null {
  return parseSofDate(order.dateRequired) ?? parseSofDate(order.dateSort);
}
